import { tools, categoryLabels } from "../tools/registry";
import tasks from "../tools/taskEntries.json";
import workflows from "../workflows/catalog.json";
import games from "../relax/catalog.json";

type UsageEvent = {
  eventType: "page_view" | "click" | "engagement" | "conversion";
  pageId?: string;
  actionId?: string;
  durationMs?: number;
};
const navigationEvent = "wyan:toolbox-navigation";
const actionEvent = "wyan:toolbox-action";
const conversionEvent = "wyan:toolbox-conversion";
const conversionIDs = new Set([
  "workflow_opened",
  "workflow_started",
  "workflow_completed",
  "artifact_downloaded",
]);
export type ToolUsageAction =
  | "run.started"
  | "run.success"
  | "run.failed"
  | "example"
  | "copy"
  | "download"
  | "used";
const toolActions: readonly ToolUsageAction[] = [
  "run.started",
  "run.success",
  "run.failed",
  "example",
  "copy",
  "download",
  "used",
];
const toolPaths = new Set([...tools, ...tasks].map((t) => "/tools/" + t.slug));
const toolScopes = new Set(
  [...toolPaths].map((p) => "tool." + p.slice(7).replaceAll("/", ".")),
);
const gamePaths = new Set(games.map((g) => "/relax/" + g.slug));
const gameScopes = new Set(games.map((g) => "game." + g.slug));
const publicPaths = new Set([
  "/",
  "/tools",
  "/workflows",
  "/relax",
  "/relax/slow",
  ...toolPaths,
  ...gamePaths,
  ...Object.keys(categoryLabels).map((k) => "/tools/category/" + k),
  ...workflows.map((w) => "/workflows/" + w.slug),
]);
const normalized = (path: string) =>
  path === "/" ? path : path.replace(/\/$/, "");

export function toolUsageActionId(
  pathname: string,
  action: ToolUsageAction,
): string {
  const path = normalized(pathname);
  if (!toolActions.includes(action) || !toolPaths.has(path)) return "";
  const id = `tool.${path.slice(7).replaceAll("/", ".")}.${action}`;
  return validActionId(id) ? id : "";
}
export function usedActionId(pathname: string): string {
  const path = normalized(pathname);
  if (toolPaths.has(path)) return toolUsageActionId(path, "used");
  if (gamePaths.has(path)) return "game." + path.slice(7) + ".used";
  return "";
}
export function trackToolUsage(action: ToolUsageAction): boolean {
  if (typeof window === "undefined" || !window.location) return false;
  const id = toolUsageActionId(window.location.pathname, action);
  if (!id) return false;
  trackUsageAction(id);
  return true;
}
export function usagePageId(pathname: string): string {
  const path = normalized(pathname);
  if (!publicPaths.has(path)) return "";
  return path === "/" ? "home" : path.slice(1).replaceAll("/", ".");
}
export function notifyUsageNavigation(): void {
  if (
    typeof window === "undefined" ||
    typeof window.dispatchEvent !== "function"
  )
    return;
  window.dispatchEvent(new Event(navigationEvent));
}
export function trackUsageAction(actionId: string): void {
  if (
    !validActionId(actionId) ||
    typeof window === "undefined" ||
    typeof window.dispatchEvent !== "function"
  )
    return;
  window.dispatchEvent(new CustomEvent(actionEvent, { detail: { actionId } }));
}
export const usageConversionAllowed = (actionId: string) =>
  conversionIDs.has(actionId);
export function trackUsageConversion(actionId: string): void {
  if (
    !usageConversionAllowed(actionId) ||
    typeof window === "undefined" ||
    typeof window.dispatchEvent !== "function"
  )
    return;
  window.dispatchEvent(
    new CustomEvent(conversionEvent, { detail: { actionId } }),
  );
}
const installed = new WeakMap<Window, () => void>();
export function installUsageAnalytics(): () => void {
  const existing = installed.get(window);
  if (existing) return existing;
  const pageID = () => usagePageId(window.location.pathname);
  // Serialize the first view and subsequent events so the first-party visitor Cookie is established.
  let delivery = Promise.resolve();
  const send = (event: UsageEvent) => {
    if (!pageID() || event.pageId === "") return;
    const body = JSON.stringify({ applicationId: "tools", ...event });
    delivery = delivery
      .then(() =>
        fetch("/api/analytics/events", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-WyanHub-Event": "1",
          },
          credentials: "same-origin",
          cache: "no-store",
          keepalive: true,
          referrerPolicy: "no-referrer",
          body,
        }),
      )
      .then(() => undefined)
      .catch(() => undefined);
  };
  let page = pageID(),
    used = false;
  let startedAt: number | null =
    document.visibilityState === "visible" ? performance.now() : null;
  const view = () => send({ eventType: "page_view", pageId: page });
  const flush = () => {
    if (startedAt === null) return;
    const durationMs = Math.min(
      1800000,
      Math.round(performance.now() - startedAt),
    );
    startedAt = null;
    if (durationMs >= 1000)
      send({ eventType: "engagement", pageId: page, durationMs });
  };
  const changed = () => {
    const next = pageID();
    if (next === page) return;
    flush();
    page = next;
    used = false;
    startedAt =
      document.visibilityState === "visible" ? performance.now() : null;
    view();
  };
  const action = (event: Event) => {
    const actionId = (event as CustomEvent<{ actionId?: unknown }>).detail
      ?.actionId;
    if (typeof actionId === "string" && validActionId(actionId))
      send({ eventType: "click", pageId: page, actionId });
  };
  const conversion = (event: Event) => {
    const actionId = (event as CustomEvent<{ actionId?: unknown }>).detail
      ?.actionId;
    if (typeof actionId === "string" && usageConversionAllowed(actionId))
      send({ eventType: "conversion", actionId });
  };
  const visibility = () => {
    if (document.visibilityState === "hidden") flush();
    else if (startedAt === null) startedAt = performance.now();
  };
  function interaction(event: Event) {
    if (
      used ||
      !page ||
      document.visibilityState !== "visible" ||
      !(event.target instanceof Element)
    )
      return;
    const target = event.target;
    if (
      event.type === "pointerdown" &&
      !target.closest('canvas,[role="application"],[contenteditable="true"]')
    )
      return;
    if (target.closest(':disabled,[inert],[aria-disabled="true"]')) return;
    const path = normalized(window.location.pathname),
      actionId = usedActionId(path);
    if (!actionId) return;
    if (toolPaths.has(path)) {
      const surface = target.closest<HTMLElement>(".tool-surface");
      if (
        !surface ||
        surface.closest<HTMLElement>("[data-usage-tool]")?.dataset.usageTool !==
          path.slice(7)
      )
        return;
      if (
        event.type === "click" &&
        !target.closest(
          'button,[role="button"],input[type="checkbox"],input[type="radio"],canvas,[contenteditable="true"]',
        )
      )
        return;
    } else {
      if (
        !target.closest(".relax-detail") ||
        target.closest('.relax-back,.relax-game-head,[role="tablist"]')
      )
        return;
      if (
        event.type === "click" &&
        !target.closest(
          'button,[role="button"],[role="gridcell"],canvas,[role="application"]',
        )
      )
        return;
    }
    if (
      (event.type === "input" || event.type === "change") &&
      !target.closest(
        'input:not([readonly]),textarea:not([readonly]),select,[contenteditable="true"]',
      )
    )
      return;
    // One used visit, independent of keystrokes, game moves, retries or the amount of user content.
    used = true;
    send({ eventType: "click", pageId: page, actionId });
  }
  const click = (event: Event) => {
    interaction(event);
    const target =
      event.target instanceof Element
        ? event.target.closest<HTMLElement>(
            "[data-analytics-id],button[id],a[id]",
          )
        : null;
    const actionId = target?.dataset.analyticsId || target?.id || "";
    if (validActionId(actionId))
      send({ eventType: "click", pageId: page, actionId });
  };
  view();
  window.addEventListener("popstate", changed);
  window.addEventListener(navigationEvent, changed);
  window.addEventListener(actionEvent, action);
  window.addEventListener(conversionEvent, conversion);
  window.addEventListener("pagehide", flush);
  document.addEventListener("visibilitychange", visibility);
  document.addEventListener("click", click);
  document.addEventListener("input", interaction);
  document.addEventListener("pointerdown", interaction);
  document.addEventListener("change", interaction);
  document.addEventListener("submit", interaction);
  const cleanup = () => {
    flush();
    window.removeEventListener("popstate", changed);
    window.removeEventListener(navigationEvent, changed);
    window.removeEventListener(actionEvent, action);
    window.removeEventListener(conversionEvent, conversion);
    window.removeEventListener("pagehide", flush);
    document.removeEventListener("visibilitychange", visibility);
    document.removeEventListener("click", click);
    document.removeEventListener("input", interaction);
    document.removeEventListener("pointerdown", interaction);
    document.removeEventListener("change", interaction);
    document.removeEventListener("submit", interaction);
    installed.delete(window);
  };
  installed.set(window, cleanup);
  return cleanup;
}
function validActionId(value: string): boolean {
  if (
    !value.length ||
    value.length > 80 ||
    !/^[a-zA-Z0-9][a-zA-Z0-9._:/-]*$/.test(value)
  )
    return false;
  // Known static slugs can be long; opaque identifiers remain rejected by the legacy guard.
  const suffix = toolActions.find((a) => value.endsWith("." + a));
  const scope = suffix ? value.slice(0, -suffix.length - 1) : "";
  if (toolScopes.has(scope) || (suffix === "used" && gameScopes.has(scope)))
    return true;
  return !/[a-z0-9]{12,}/i.test(value);
}
